// src/manage/index.js
import React, { useEffect, useState, useCallback } from 'react';
import {
    Box,
    CircularProgress,
    Container,
    Typography,
    Alert,
    AlertTitle,
} from '@mui/material';
import FooterComponent from '../components/common/footerComponent';
import HeaderComponent from '../components/common/header';
import EditDialog from './add-edit-dialog';
import AddProjectDialog from './add-project-dialog';
import TableComponent from './tableComponent';
import JSZip from 'jszip';
import { useTranslation } from 'react-i18next';

const ManagePage = ({ instance }) => {
    const [ddnsData, setDdnsData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAddMode, setIsAddMode] = useState(false);
    const [isAddProjectMode, setIsAddProjectMode] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [openAddProjectDialog, setOpenAddProjectDialog] = useState(false);
    const [selectProject, setSelectProject] = useState('');
    const [alert, setAlert] = useState({ open: false, severity: '', message: '' });
    const baseUrl = window.location.origin;
    const apiBaseUrl = `${baseUrl}/api`;
    const { t } = useTranslation();

    const handleCloseAlert = () => {
        setAlert({ open: false, severity: '', message: '' });
    };

    const handleAdd = () => {
        setIsAddMode(true);
        setSelectedRow(null);
        setOpenDialog(true);
    };
    const handleAddProject = () => {
        setIsAddProjectMode(true);
        setSelectedRow(null);
        setOpenAddProjectDialog(true);
    };

    const handleEditProject = (row) => {
        setSelectedRow(row);
        setIsAddProjectMode(false);
        setOpenAddProjectDialog(true);
    };

    const handleEdit = (row) => {
        setIsAddMode(false);
        setSelectedRow(row);
        setOpenDialog(true);
    };

    const fetchDDNSData = useCallback(async () => {
        try {
            const response = await fetch(`${apiBaseUrl}/ddnsupdater`);
            if (!response.ok) {
                setAlert({ open: true, severity: 'error', message: response.body });
            }
            const ddnsdata = await response.json();
            setDdnsData(ddnsdata.data);
        } catch (err) {
            setError(err.message);
            setAlert({ open: true, severity: 'error', message: err.message });
        } finally {
            setLoading(false);
        }
    }, [apiBaseUrl]);

    const handleSaveProject = async (editedData) => {
        try {
            setLoading(true);
            const method = isAddProjectMode ? 'POST' : 'PUT';
            const url = `${apiBaseUrl}/projects`;
            // Destructure specific properties from editedData
            const { project, description, id } = editedData;
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ project, description, id }),
            });

            if (response.ok === false) {
                setAlert({ open: true, severity: 'error', message: response.body });
            }

        } catch (err) {
            setError(err.message);
            setAlert({ open: true, severity: 'error', message: error });
        } finally {
            setLoading(false);
            setAlert({ open: true, severity: 'success', message: t('Savesuccessful') });
            await fetchDDNSData();
        }
    };

    const handleSave = async (editedData) => {
        try {
            setLoading(true);
            const method = isAddMode ? 'POST' : 'PUT';
            const url = `${apiBaseUrl}/ddnsupdater`;
            // Destructure specific properties from editedData
            const { id, ip, subDomain, description, timeout, isDisabled, project } = editedData;
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ id, ip, subDomain, description, timeout, isDisabled, project }),
            });

            if (response.ok === false) {
                setAlert({ open: true, severity: 'error', message: response.body });
            }

        } catch (err) {
            setError(err.message);
            setAlert({ open: true, severity: 'error', message: error });
        } finally {
            setLoading(false);
            setAlert({ open: true, severity: 'success', message: t('Savesuccessful') });
            await fetchDDNSData();

        }
    };

    const handleDelete = async (id, subDomain, project) => {
        try {
            setLoading(true);
            let response = null;
            if (subDomain !== null) {
                response = await fetch(`${apiBaseUrl}/ddnsupdater/`, {
                    method: 'DELETE',
                    body: JSON.stringify({ id, subDomain })
                });
            }
            else {
                response = await fetch(`${apiBaseUrl}/projects/`, {
                    method: 'DELETE',
                    body: JSON.stringify({ id, project })
                });

            }

            if (response.ok === false) {
                setAlert({ open: true, severity: 'error', message: response.body });
            }
        } catch (err) {
            setError(err.message);
            setAlert({ open: true, severity: 'error', message: error });
        } finally {
            setLoading(false);
            setAlert({ open: true, severity: 'success', message: t('Deletesuccessful') });
            await fetchDDNSData();

        }
    };

    const handleDownload = async (subdomain, timeout, id) => {
        console.log(subdomain + timeout + id);
        try {
            // Get token from Azure AD (using MSAL)
            // const token = await getAzureADToken();

            // Add authentication headers
            const headers = {
                // Authorization: `Bearer ${token}`,
                'Content-Type': 'application/zip'
            };
            // Fetch the existing ZIP file
            const zipResponse = await fetch('https://bridgesddnsafe4.blob.core.windows.net/ddns-node-runner/ddnsnoderunner_2030.zip?sp=r&st=2025-03-30T14:27:14Z&se=2030-03-30T22:27:14Z&spr=https&sv=2024-11-04&sr=b&sig=AxCN9%2FKZGw9rpnW3xqFkRB8iwxccNaza9iZqNkQE8NM%3D', {
                method: 'GET',
                headers: headers,
                credentials: 'include'
            });
            if (!zipResponse.ok) {
                setAlert({ open: true, severity: 'error', message: 'Failed to download ZIP file' });
                return;
            }
            const zipBlob = await zipResponse.blob();
            const zip = await JSZip.loadAsync(zipBlob);
            // Fetch the config.js content
            const configResponse = await fetch(`${apiBaseUrl}/generatefile`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ subdomain, timeout, id }),
            });

            if (!configResponse.ok) {
                setAlert({ open: true, severity: 'error', message: 'Failed to generate config.js' });
                return;
            }

            const configText = await configResponse.text();

            // Add config.js to the ZIP file
            zip.file('config.js', configText);

            // Generate the updated ZIP file
            const updatedZipBlob = await zip.generateAsync({ type: 'blob' });
            const url = window.URL.createObjectURL(updatedZipBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `ddnsnoderunner_${subdomain}.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (err) {
            setError(err.message);
            setAlert({ open: true, severity: 'error', message: err.message });
        }
    };

    useEffect(() => {
        fetchDDNSData();
    }, [fetchDDNSData]);


    if (error) {
        return (
            <Container>
                <Box mt={4}>
                    <Typography color="error" variant="h6">
                        Error: {error}
                    </Typography>
                </Box>
            </Container>
        );
    }


    return (
        <>

            <HeaderComponent />
            {loading && (
                <div className="loading-overlay">
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                        <CircularProgress />
                    </Box>
                </div>
            )}
            {!loading && (
                <Box mt={4} mb={4} style={{ padding: '100px 20px 20px 20px' }}>
                    {alert.open && (
                        <Alert severity={alert.severity} onClose={handleCloseAlert} sx={{ mb: 2 }}>
                            <AlertTitle>{alert.severity === 'success' ? t('Success') : t('Error')}</AlertTitle>
                            {alert.message}
                        </Alert>
                    )}
                    <TableComponent
                        data={ddnsData}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onDownload={handleDownload}
                        onEnableDisable={handleSave}
                        onProjectSelect={setSelectProject}
                        onProjectclick={handleAdd}
                        onAddProjectclick={handleAddProject}
                        onProjectEdit={handleEditProject}
                        onRefresh={fetchDDNSData}
                    />
                    <EditDialog
                        data={ddnsData}
                        open={openDialog}
                        row={selectedRow}
                        onClose={() => {
                            setOpenDialog(false);
                            setIsAddMode(false);
                        }}
                        onSave={handleSave}
                        isEdit={!isAddMode}
                        selectedProject={selectProject}
                    />
                    <AddProjectDialog
                        data={ddnsData}
                        open={openAddProjectDialog}
                        row={selectedRow}
                        onClose={() => {
                            setOpenAddProjectDialog(false);
                            setIsAddProjectMode(false);
                        }}
                        isEdit={!isAddProjectMode}
                        onSave={handleSaveProject}
                    />
                </Box>
            )}
            <FooterComponent />
        </>
    );
};

export default ManagePage;